import {
  AppBar,
  Box,
  Toolbar,
  Tabs,
  useTheme,
  useMediaQuery,
  IconButton,
  Badge as MUIBadge,
  styled,
} from '@mui/material';
import { useState, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Dashboard, DirectionsCar, Explore, Flight, Hotel, Notifications, ShoppingCart } from '@mui/icons-material';

import { getAgentStatusColor } from '@frontend/features/users/helpers';
import { AgentStatus } from '@bandido/shared/constants';

import { routes } from '@frontend/router/routes';

import { disconnectedUser, logo, logoDesktop } from '../../../../assets';
import { AvatarHolder, CustomAvatar, NavBarButton, NotificationsHolder, Tab } from './styles';
import { UserMenu } from '../UserMenu';
import { useAuth } from '../../../auth/hooks/useAuth';
import { useUpdateHeaderHeight } from '../../../dashboard/hooks/useHeaderHeight';
import { Badge } from '../Badge';
import { NotificationsDrawer } from '../NotificationsDrawer';
import { useCart } from '@frontend/features/cart/components/CartContext';
import { HotelSearchBar } from '@frontend/features/hotel/components/Availability/Search/HotelSearchBar';
import { useSidebar } from '@frontend/features/dashboard/contexts/SidebarContext/hook';
import {
  FLIGHT_SEARCH_UNDER_HEADER_HEIGHT,
  HEADER_HEIGHT,
  HOTEL_SEARCH_UNDER_HEADER_HEIGHT,
} from '../../constants/layout';
import { FlightSearchHeader } from '@frontend/features/flight/components/FlightSearchHeader';
import { LogoHeader } from './LogoHeader';

export const HotelSearchBox = styled('div')<{ isOpen: boolean }>(({ theme, isOpen }) => ({
  position: 'fixed',
  width: '100%',
  height: `${HOTEL_SEARCH_UNDER_HEADER_HEIGHT}px`,
  top: isOpen ? `${HEADER_HEIGHT}px` : `-${HOTEL_SEARCH_UNDER_HEADER_HEIGHT}px`,
  transition: 'top 0.4s',
  display: 'flex',
  justifyContent: 'center',
  zIndex: theme.zIndex.appBar - 1,
  backgroundColor: theme.palette.primary.main,
}));

export const FLightsSearchBox = styled('div')<{ isOpen: boolean }>(({ theme, isOpen }) => ({
  position: 'fixed',
  width: '100%',
  minHeight: `${FLIGHT_SEARCH_UNDER_HEADER_HEIGHT}px`,
  top: isOpen ? `${HEADER_HEIGHT}px` : `-${FLIGHT_SEARCH_UNDER_HEADER_HEIGHT}px`,
  transition: 'top 0.4s',
  display: 'flex',
  justifyContent: 'center',
  zIndex: theme.zIndex.appBar - 1,
  backgroundColor: theme.palette.primary.main,
}));

export const SearchBarContainer = styled('div')(({ theme }) => ({
  width: '100%',
  maxWidth: '1156px',
  display: 'flex',
  gap: theme.spacing(2),
  margin: theme.spacing(2),
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
}));

interface Props {
  onOpenHotelSearch: (value: boolean) => void;
  onOpenFlightsSearch: (value: boolean) => void;
}

export const Header = ({ onOpenHotelSearch, onOpenFlightsSearch }: Props) => {
  const location = useLocation();
  const { ability, myProfile } = useAuth();
  const [value, setValue] = useState(0);
  const notificationIconRef = useRef<HTMLDivElement | null>(null);
  const [hotelSearchOpen, setHotelSearchOpen] = useState(false);
  const [flightsSearchOpen, setFlightsSearchOpen] = useState(false);
  const { setHasHotelNavSearch, setHasFlightsNavSearch, hasNavFlightsSearch, hasNavHotelSearch } = useSidebar();

  const handleChange = (_: any, newValue: number) => {
    setValue(newValue);
  };
  const [open, setOpen] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const theme = useTheme();

  const { state } = useCart();

  const cartItemQty = state?.items?.length || 0;
  const isSmall = useMediaQuery(theme.breakpoints.up('sm'));
  const isMedium = useMediaQuery(theme.breakpoints.up('md'));
  const isLarge = useMediaQuery(theme.breakpoints.up('lg'));
  const postaBrakepoint = useMediaQuery('(min-width:758px)');
  const { t } = useTranslation();
  const navigate = useNavigate();
  const headerRef = useRef<HTMLElement>(null);

  const handleNavigate = (route: string) => () => {
    navigate(route, { replace: true });
  };

  const toggleNotifications = () => setNotificationOpen((prevState) => !prevState);

  const toggleDrawer = () => setOpen((prevState) => !prevState);

  useUpdateHeaderHeight(headerRef);

  const agentStatus = myProfile?.status;

  const onHotelClick = useCallback(() => {
    setHotelSearchOpen((prev) => {
      const newValue = !prev;
      onOpenHotelSearch(newValue);
      setHasHotelNavSearch(newValue);
      return newValue;
    });
    if (flightsSearchOpen) {
      setFlightsSearchOpen(false);
      onOpenFlightsSearch(false);
      setHasFlightsNavSearch(false);
    }
  }, [hotelSearchOpen, flightsSearchOpen]);

  const onFlightsClick = useCallback(() => {
    setFlightsSearchOpen((prev) => {
      const newValue = !prev;
      onOpenFlightsSearch(newValue);
      setHasFlightsNavSearch(newValue);
      return newValue;
    });
    if (hotelSearchOpen) {
      setHotelSearchOpen(false);
      onOpenHotelSearch(false);
      setHasHotelNavSearch(false);
    }
  }, [hotelSearchOpen, flightsSearchOpen]);

  return (
    <>
      <AppBar position="fixed" ref={headerRef}>
        <Box pl={isLarge ? 9 : 2} pr={isLarge ? 9 : 2}>
          <Toolbar disableGutters sx={{ display: 'flex', justifyContent: 'space-between' }}>
            {/* TODO: refactor to use mui components */}

            <LogoHeader 
              src={isLarge ? logoDesktop : logo}
              onClick={handleNavigate(routes.home)}
            />

            {isLarge && (
              <Box
                flexGrow={1}
                display={isMedium ? ' flex' : 'column'}
                justifyContent={isMedium ? 'center !important' : 'space-around'}
              >
                {ability && ability.can('view', 'Flights') && (
                  <NavBarButton
                    sx={{
                      borderRadius: 100,
                    }}
                    className={hasNavFlightsSearch ? 'active' : ''}
                    size="small"
                    variant="outlined"
                    color="primary"
                    disableFocusRipple={true}
                    disableRipple
                    onClick={onFlightsClick}
                    startIcon={<Flight />}
                  >
                    {t('common.nav.flights')}
                  </NavBarButton>
                )}
                {ability && ability.can('view', 'Hotels') && (
                  <NavBarButton
                    sx={{
                      borderRadius: 100,
                    }}
                    className={hasNavHotelSearch ? 'active' : ''}
                    size="small"
                    variant="outlined"
                    color="primary"
                    disableFocusRipple={true}
                    disableRipple
                    onClick={onHotelClick}
                    startIcon={<Hotel />}
                  >
                    {t('common.nav.hotels')}
                  </NavBarButton>
                )}
                {ability && ability.can('view', 'Cars') && (
                  <NavBarButton
                    sx={{
                      borderRadius: 100,
                    }}
                    className={location.pathname.includes(routes.cars) ? 'active' : ''}
                    size="small"
                    variant="outlined"
                    color="primary"
                    disableFocusRipple={true}
                    disableRipple
                    // onClick={handleNavigate(routes.cars)}
                    startIcon={<DirectionsCar />}
                  >
                    {t('common.nav.cars')}
                  </NavBarButton>
                )}
                {ability && ability.can('view', 'Explorer') && (
                  <NavBarButton
                    sx={{
                      borderRadius: 100,
                    }}
                    className={location.pathname.includes(routes.explorer) ? 'active' : ''}
                    size="small"
                    variant="outlined"
                    color="primary"
                    disableFocusRipple={true}
                    disableRipple
                    // onClick={handleNavigate(routes.explorer)}
                    startIcon={<Explore />}
                  >
                    {t('common.nav.explorer')}
                  </NavBarButton>
                )}
                {ability && ability.can('view', 'Dashboard') && (
                  <NavBarButton
                    sx={{
                      borderRadius: 100,
                    }}
                    className={location.pathname.includes(routes.dashboard.index) ? 'active' : ''}
                    size="small"
                    variant="outlined"
                    color="primary"
                    disableFocusRipple={true}
                    disableRipple
                    onClick={handleNavigate(routes.dashboard.index)}
                    startIcon={<Dashboard />}
                  >
                    {t('common.nav.dashboard')}
                  </NavBarButton>
                )}
              </Box>
            )}

            {/* Always show user actions (notifications, cart, avatar) */}
            <Box display="flex" alignItems="center" gap={isLarge ? 1 : 0.5}>
              {myProfile && (
                <NotificationsHolder ref={notificationIconRef}>
                  <IconButton 
                    onClick={toggleNotifications} 
                    disableFocusRipple 
                    disableRipple
                    size={isLarge ? "medium" : "small"}
                  >
                    <MUIBadge
                      invisible={myProfile.notifications === '0'}
                      badgeContent={myProfile.notifications}
                      color="error"
                    >
                      <Notifications htmlColor={theme.palette.common.white} />
                    </MUIBadge>
                  </IconButton>
                </NotificationsHolder>
              )}
              <NotificationsHolder>
                <IconButton 
                  onClick={() => navigate(routes.salesorder.cart)} 
                  disableFocusRipple 
                  disableRipple
                  size={isLarge ? "medium" : "small"}
                >
                  <MUIBadge invisible={cartItemQty === 0} badgeContent={cartItemQty} color="error">
                    <ShoppingCart htmlColor={theme.palette.common.white} />
                  </MUIBadge>
                </IconButton>
              </NotificationsHolder>
              <AvatarHolder>
                <IconButton 
                  onClick={toggleDrawer} 
                  disableFocusRipple 
                  disableRipple
                  size={isLarge ? "medium" : "small"}
                >
                  <Badge color={agentStatus && getAgentStatusColor(agentStatus as AgentStatus, theme)}>
                    <CustomAvatar 
                      src={myProfile?.profilePicture || disconnectedUser}
                      sx={!isLarge ? { width: 32, height: 32 } : {}}
                    />
                  </Badge>
                </IconButton>
              </AvatarHolder>
            </Box>
            <NotificationsDrawer
              notificationIconRef={notificationIconRef}
              open={notificationOpen}
              onClose={toggleNotifications}
            />
            <UserMenu open={open} toggle={toggleDrawer} />
          </Toolbar>
        </Box>

        {!isLarge && (
          <Box 
            display="flex" 
            justifyContent="center" 
            alignItems="flex-start" 
            pl={postaBrakepoint ? 2 : 0} 
            pr={postaBrakepoint ? 2 : 0}
          >
            <Tabs
              variant={postaBrakepoint ? "scrollable" : "fullWidth"}
              centered={!postaBrakepoint}
              aria-label="menu de motores" // TODO: add translation
              allowScrollButtonsMobile={postaBrakepoint}
              scrollButtons={postaBrakepoint}
              value={value}
              onChange={handleChange}
              sx={{
                minHeight: 'auto',
                '& .MuiTabs-flexContainer': {
                  gap: 0.5,
                  justifyContent: !postaBrakepoint ? 'center' : 'flex-start',
                },
                '& .MuiTab-root': {
                  minWidth: 'auto',
                  padding: postaBrakepoint ? '6px 12px' : '6px 8px',
                  '& .MuiSvgIcon-root': {
                    fontSize: postaBrakepoint ? '1.5rem' : '1.8rem',
                  }
                }
              }}
            >
              <Tab
                icon={<Flight />}
                onClick={onFlightsClick}
                iconPosition={postaBrakepoint && isSmall ? 'start' : 'top'}
                label={postaBrakepoint ? t('common.nav.flights') : ''}
              />
              <Tab
                icon={<Hotel />}
                onClick={onHotelClick}
                iconPosition={postaBrakepoint && isSmall ? 'start' : 'top'}
                label={postaBrakepoint ? t('common.nav.hotels') : ''}
              />
              <Tab
                icon={<DirectionsCar />}
                // onClick={handleNavigate(routes.cars)}
                iconPosition={postaBrakepoint && isSmall ? 'start' : 'top'}
                label={postaBrakepoint ? t('common.nav.cars') : ''}
              />
              <Tab
                icon={<Explore />}
                // onClick={handleNavigate(routes.explorer)}
                iconPosition={postaBrakepoint && isSmall ? 'start' : 'top'}
                label={postaBrakepoint ? t('common.nav.explorer') : ''}
              />
              {ability && ability.can('view', 'Dashboard') && (
                <Tab
                  icon={<Dashboard />}
                  onClick={handleNavigate(routes.dashboard.index)}
                  iconPosition={postaBrakepoint && isSmall ? 'start' : 'top'}
                  label={postaBrakepoint ? t('common.nav.dashboard') : ''}
                />
              )}
            </Tabs>
          </Box>
        )}
      </AppBar>
      {hotelSearchOpen && (
        <HotelSearchBox isOpen={hotelSearchOpen}>
          <SearchBarContainer>
            <HotelSearchBar />
          </SearchBarContainer>
        </HotelSearchBox>
      )}
      {flightsSearchOpen && (
        <FLightsSearchBox isOpen={flightsSearchOpen}>
          <SearchBarContainer>
            <FlightSearchHeader />
          </SearchBarContainer>
        </FLightsSearchBox>
      )}
    </>
  );
};
